import { useEffect, useState } from "react";
import { Toast } from "src/components";
import { getImageDimensions, encryptFile, prepareRoomEncryptionForSend } from "../utils";
import { EventTimeline } from "matrix-js-sdk";
import { extractVideoThumbnail } from "../utils";
import { encodeUnpaddedBase64 } from "../utils/matrixBase64";

const MAX_ATTACHMENT_SIZE = 500 * 1024 * 1024;

/**
 * @typedef {import('matrix-js-sdk').MatrixClient} MatrixClient
 * @param {MatrixChat} currentChat
 * @param {MatrixClient} matrixClient
 */
export const useSendMessage = (currentChat, matrixClient) => {
    const [newMessage, setNewMessage] = useState("");
    const [attachments, setAttachments] = useState([]);
    /*https://spec.matrix.org/v1.14/client-server-api/#threading */
    const [newMessageInReplyToEvent, setNewMessageInReplyToEvent] = useState(null);

    /** https://spec.matrix.org/v1.14/client-server-api/#event-replacements */
    const [editMessageEvent, setEditMessageEvent] = useState(null);
    const [queued, setQueued] = useState(null);

    const [attachmentQueue, setAttachmentQueue] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [resentEventIds, setResentEventIds] = useState(() => new Set());

    const getExpectedRoomUserIds = () => {
        return (currentChat?.users || [])
            .map(user => user.matrixUserId)
            .filter(Boolean);
    };

    useEffect(() => {
        if (queued?.isSending && queued?.status === null && !queued.isFile) {
            sendMessage(queued);
        }
    }, [queued]);

    const prepareMessage = () => {
        const hasText = newMessage?.trim() !== "";
        const hasFiles = attachments.length > 0;
        if (!hasText && !hasFiles) return;

        const data = {
            message: hasText ? newMessage.trim() : null,
            isSending: true,
            status: null,
            isFile: hasFiles ? true : null,
            attachments: [...attachments],
            inReplyToEventId: newMessageInReplyToEvent?.getId(),
            editedEventId: editMessageEvent?.getId(),
        };

        setNewMessage("");
        setAttachments([]);
        onResetInputMessageEvents();

        setQueued(data);
        setAttachmentQueue([...attachments]);
    };

    const sendMessage = async (data) => {
        const { message, attachments, inReplyToEventId, editedEventId } = data;

        try {
            const room = matrixClient.getRoom(currentChat.matrixRoomId);
            await prepareRoomEncryptionForSend(matrixClient, room, getExpectedRoomUserIds());
            const originalEvent = room?.findEventById(editedEventId);
            const OrigBody = originalEvent?.getContent()?.body;

            let basePayload = {
                body: message,
                msgtype: "m.text",
            };

            if (inReplyToEventId) {
                basePayload["m.relates_to"] = {
                    "m.in_reply_to": { event_id: inReplyToEventId },
                };
            }

            if (editedEventId) {
                const originalContent = originalEvent?.getContent();
                const originalReplyTo = originalContent?.["m.relates_to"]?.["m.in_reply_to"]; // Preserve the original in-reply-to relationship, if any

                const relatesTo = {
                    rel_type: "m.replace",
                    event_id: editedEventId,
                };

                if (inReplyToEventId || originalReplyTo?.event_id) {
                    relatesTo["m.in_reply_to"] = {
                        event_id: inReplyToEventId || originalReplyTo.event_id,
                    };
                }

                basePayload = {
                    ...basePayload,
                    "m.new_content": {
                        body: message,
                        msgtype: "m.text",
                    },
                    "m.relates_to": relatesTo,
                    "m.old_message_body": OrigBody, // Preserve the original message content for history tracking
                };
                // Automatically redact the original edited event so it's no longer displayed in the UI
            }

            if (message) {
                await matrixClient.sendMessage(currentChat.matrixRoomId, basePayload);
            }

            const events = room?.getLiveTimeline()?.getEvents() || [];
            const lastSentEvent = [...events].reverse().find(e => !!e.getId() && e.status !== "not_sent");
            if (
                lastSentEvent &&
                matrixClient.getVisibleRooms?.().includes(currentChat.matrixRoomId)
            ) {
                await matrixClient.sendReadReceipt(lastSentEvent);
            }
        } catch (err) {
            console.error(err);
            setQueued(prev => ({ ...prev, msgType: "m.text", status: "failed", isSending: false, failureSource: "sendMessage" }));
            setNewMessage(null);
            console.warn("Unable to send the message.");
        } finally {
            if (attachments.length > 0) {
                setAttachmentQueue([...attachments]);
            } else if (!queued?.isFile) {
                setQueued(null);
            }
        }
    };

    // Upload each file one by one
    useEffect(() => {
        const processNextAttachment = async () => {
            if (attachmentQueue.length === 0 || isUploading) {
                return;
            }

            setIsUploading(true);
            let localQueue = [...attachmentQueue];

            try {
                while (localQueue.length > 0) {
                    const nextFileObj = localQueue[0];
                    const file = nextFileObj?.file;
                    if (!file) {
                        localQueue.shift();
                        setAttachmentQueue(localQueue);
                        continue;
                    }

                    setQueued({
                        message: nextFileObj,
                        status: "sending",
                        isSending: true,
                        isFile: file.type,
                    });

                    try {
                        const room = matrixClient.getRoom(currentChat.matrixRoomId);
                        await prepareRoomEncryptionForSend(matrixClient, room, getExpectedRoomUserIds());
                        const state = room?.getLiveTimeline()?.getState(EventTimeline.FORWARDS);
                        const isRoomEncrypted = !!state?.getStateEvents("m.room.encryption", "");
                        if (!isRoomEncrypted) {
                            throw new Error("This room is not encrypted. Please create a new encrypted room for secure messaging.");
                        }
                        if (!matrixClient?.getCrypto()) {
                            throw new Error("Encryption system not ready. Please try again later.");
                        }

                        await handleFileUpload(file, true);
                        setQueued(null);
                    } catch (err) {
                        setQueued({
                            message: nextFileObj,
                            status: "failed",
                            isSending: false,
                            isFile: file.type,
                            failureSource: "uploadContent"
                        });
                        console.error(err);
                        Toast.error(err.message || "Unable to send attachment.");
                    } finally {
                        localQueue.shift();
                        setAttachmentQueue(localQueue);
                    }
                }
            } finally {
                setIsUploading(false);
            }
        };

        if (attachmentQueue.length > 0 && !isUploading) {
            processNextAttachment();
        }
    }, [attachmentQueue, isUploading]);

    const handleFileUpload = async (file, shouldEncrypt) => {
        if (!queued) return;

        const caption = typeof queued.message === "string" ? queued.message.trim() : null;
        const inReplyToEventId = queued.inReplyToEventId;
        const editedEventId = queued.editedEventId;

        if (file.size > MAX_ATTACHMENT_SIZE) {
            throw new Error("Attachment too large. Max 500MB allowed.");
        }

        const dimensions = await getImageDimensions(file);
        const info = {
            mimetype: file.type,
            size: file.size,
        };

        let msgtype = "m.file";
        if (file.type.startsWith("image/")) {
            msgtype = "m.image";
            if (dimensions) {
                info.w = dimensions.width;
                info.h = dimensions.height;
            }
        } else if (file.type.startsWith("video/")) {
            msgtype = "m.video";

            const { blob: thumbBlob, width, height } =
                await extractVideoThumbnail(file, { asBlob: true });
            const { ciphertext, iv, keyJwk, hash } = await encryptFile(thumbBlob);

            const upload = await matrixClient.uploadContent(ciphertext, {
                name: `thumb_${file.name}`,
                type: "image/jpeg",
            });

            info.thumbnail_file = {
                url: upload.content_uri,
                v: "v2",
                key: {
                    ...keyJwk,
                    alg: "A256CTR",
                    key_ops: ["encrypt", "decrypt"],
                    ext: true,
                },
                iv: encodeUnpaddedBase64(iv),
                hashes: { sha256: encodeUnpaddedBase64(hash) },
            };

            info.thumbnail_info = {
                w: width,
                h: height,
                mimetype: "image/jpeg",
            };
        }

        let payload;

        if (shouldEncrypt) {
            const { ciphertext, iv, keyJwk, hash } = await encryptFile(file);
            const upload = await matrixClient.uploadContent(ciphertext, {
                name: file.name,
                type: file.type,
                uploadTimeoutMs: 300000,
                rawResponse: false,
            });

            const contentUri = typeof upload === "string" ? upload : upload?.content_uri;
            if (!contentUri) throw new Error("Encrypted upload failed");

            payload = {
                msgtype,
                body: file.name,
                file: {
                    url: contentUri,
                    v: "v2",
                    key: {
                        ...keyJwk,
                        alg: "A256CTR",
                        key_ops: ["encrypt", "decrypt"],
                        ext: true,
                    },
                    iv: encodeUnpaddedBase64(iv),
                    hashes: { sha256: encodeUnpaddedBase64(hash) },
                },
                info,
            };
        }
        setQueued(prev => ({
            ...prev,
            status: "ready",
            isSending: true,
            isFile: file.type,
        }));

        if (caption) {
            const captionPayload = {
                msgtype: "m.text",
                body: caption,
            };
            if (inReplyToEventId) {
                captionPayload["m.relates_to"] = { "m.in_reply_to": { event_id: inReplyToEventId } };
            }
            if (editedEventId) {
                captionPayload["m.relates_to"] = { rel_type: "m.replace", event_id: editedEventId };
            }
            await matrixClient.sendMessage(currentChat.matrixRoomId, captionPayload);
        }

        await matrixClient.sendMessage(currentChat.matrixRoomId, payload);
    };

    //Resend
    const resendMessage = async (event) => {
        if (!event) return;
        const content = event.getContent();
        const roomId = event.getRoomId();
        const isFile = Boolean(content?.info?.mimetype && (content?.url || content?.file?.url));

        // Show the loading resend
        setQueued({
            message: content?.body || { file: { name: content?.body, type: content?.info?.mimetype } },
            status: "sending",
            isSending: true,
            isFile,
            msgType: content?.msgtype,
            failureSource: "sendMessage",
        });

        try {
            await matrixClient.sendEvent(roomId, "m.room.message", content);
            setResentEventIds(prev => new Set(prev).add(event.getId()));
            setQueued(null);

        } catch (err) {
            console.error("Failed to resend original message:", err);
            setQueued(prev => ({
                ...prev,
                status: "failed",
                isSending: false,
            }));
            Toast.error("Unable to resend message.");
        }
    };

    const onResetInputMessageEvents = () => {
        setNewMessageInReplyToEvent(null);
        setEditMessageEvent(null);
    };

    const onSetNewMessageInReplyToEventHandler = (value) => {
        setNewMessage(null);
        setNewMessageInReplyToEvent(value);
        setEditMessageEvent(null);
    };

    const onSetEditMessageEventHandler = (value) => {
        setNewMessage(null);
        setEditMessageEvent(value);
        setNewMessageInReplyToEvent(null);
    };

    return {
        newMessage,
        queued,
        newMessageInReplyToEvent,
        editMessageEvent,
        attachments,
        setNewMessage,
        prepareMessage,
        onSetNewMessageInReplyToEventHandler,
        onSetEditMessageEventHandler,
        onResetInputMessageEvents,
        setAttachments,
        resendMessage,
        resentEventIds
    };
};
